import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import cs from 'classnames';

import { getAiTask } from 'api/Admin/intelligence/aiTask';

import { MapView } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/MyScribeTemplate/components/chat/ResponseMapView';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Modal from 'components/shared/Modal/Modal';
import Switch from 'components/shared/Switch/Switch';
import Textarea from 'components/shared/Textarea/Textarea';

import AiTaskRequestResponseView from '../components/AiTaskRequestResponseView';

const AdminIntelligenceAiTaskModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [aiTaskId, setAiTaskId] = useState();
  const [selectedAiTask, setSelectedAiTask] = useState();
  const [show, setShow] = useState({ loading: false, original: false, mapped: false });
  const navigate = useNavigate();

  const aiTaskRequestRef = useRef();
  const aiTaskResponseRef = useRef();

  useEffect(() => {
    if (searchParams.get('aiTaskId')) {
      getData();
    }
  }, [searchParams]);

  const getData = async () => {
    if (show.loading) return;
    setShow((ps) => ({ ...ps, loading: true }));
    const taskId = parseInt(searchParams.get('aiTaskId'), 10);
    setAiTaskId(taskId);
    const res = await getAiTask(navigate, { id: taskId });
    setSelectedAiTask(res.data);
    setShow((ps) => ({ ...ps, loading: false }));
  };

  return (
    <Modal
      slideFromRight
      isOpen={aiTaskId}
      bodyClassName="!p-0"
      title={`AI Task #${aiTaskId}`}
      className={cs('bg-white', 'w-9/12')}
      handleClose={() => {
        setAiTaskId(null);
        setSearchParams((sp) => sp.delete('aiTaskId'));
      }}>
      {show?.loading && (
        <div className="loadingSpinnerWrapper flex h-full w-full items-center justify-center">
          <div className="loadingSpinner"></div>
        </div>
      )}
      {!show?.loading && (
        <div className="flex h-full flex-col gap-4 bg-neutral-100 p-4">
          <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
            <div className="flex flex-row items-center gap-4">
              <Switch
                label="Show Original String"
                checked={show.original}
                color="primary"
                parentContainerClassName="flex gap-2 items-center font-bold"
                onChange={() => setShow((ps) => ({ ...ps, original: !ps.original }))}
              />
              {!!selectedAiTask?.isNote && (
                <Switch
                  label="Show Mapped Response"
                  checked={show.mapped}
                  color="primary"
                  parentContainerClassName="flex gap-4 items-center font-bold"
                  onChange={() => setShow((ps) => ({ ...ps, mapped: !ps.mapped }))}
                />
              )}
              <Button
                outlined
                disabled={show?.loading}
                shade="700"
                text="Refresh"
                color="neutral"
                icon="update-mono"
                onClick={getData}
                loadingIcon={show?.loading}
                textClassName="text-sm leading-[114%] text-neutral-700"
                className={cs(
                  'z-50 h-[32px] border-neutral-200 px-3 py-0',
                  'cursor-pointer hover:border-neutral-300 hover:bg-neutral-50'
                )}
              />
            </div>
            <div className="flex h-full flex-row items-center justify-center gap-4">
              <div className="flex h-full w-1/2 flex-col gap-2 border-2">
                <label className="font-bold">Request</label>
                {!show.original ? (
                  <AiTaskRequestResponseView type="Request" html={selectedAiTask?.request} />
                ) : (
                  <Textarea
                    forwardedRef={aiTaskRequestRef}
                    value={selectedAiTask?.request}
                    disabled
                  />
                )}
              </div>
              <div className="flex h-full w-1/2 flex-col gap-2 border-2">
                <label className="font-bold">Response</label>
                {!!show.mapped && selectedAiTask?.note ? (
                  <MapView note={selectedAiTask?.note} />
                ) : (
                  <>
                    {!show.original ? (
                      <AiTaskRequestResponseView type="Response" html={selectedAiTask?.response} />
                    ) : (
                      <Textarea
                        forwardedRef={aiTaskResponseRef}
                        value={selectedAiTask?.response}
                        disabled
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default withErrorBoundary(AdminIntelligenceAiTaskModal);
